import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';

const ResetPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // ForgotPasswordPageから渡されたemailを取得
  React.useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !code || !newPassword || !confirmPassword) {
      setError('すべての項目を入力してください');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (newPassword.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authApi.resetPassword(email, code, newPassword);
      navigate('/login', { 
        state: { 
          message: 'パスワードがリセットされました。新しいパスワードでログインしてください。' 
        } 
      });
    } catch (err: any) {
      if (err.response?.data?.error?.includes('CodeMismatchException')) {
        setError('確認コードが正しくありません');
      } else if (err.response?.data?.error?.includes('ExpiredCodeException')) {
        setError('確認コードの有効期限が切れています。再度パスワードリセットを行ってください。');
      } else if (err.response?.data?.error?.includes('InvalidPasswordException')) {
        setError('パスワードが条件を満たしていません。8文字以上で、大文字・小文字・数字・記号を含めてください。');
      } else {
        setError(err.response?.data?.error || 'エラーが発生しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('メールアドレスを入力してください');
      return;
    }

    try {
      await authApi.forgotPassword(email);
      setError('');
      // 成功メッセージは表示しない（セキュリティ上の理由）
    } catch (err: any) {
      setError('再送信に失敗しました');
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center">
            パスワードリセット
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            メールで受信した確認コードと新しいパスワードを入力してください
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="メールアドレス"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="code"
              label="確認コード"
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="メールで受信したコードを入力"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="newPassword"
              label="新しいパスワード"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="8文字以上で、大文字・小文字・数字・記号を含める"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="confirmPassword"
              label="パスワード確認"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? '設定中...' : 'パスワードを設定'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleResendCode}
                sx={{ mr: 2 }}
              >
                確認コードを再送信
              </Link>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/login')}
              >
                ログインページに戻る
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
